import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { orgUnitsApi } from "../api/org-units";

const orgUnitKeys = {
  all: ["org-units"] as const,
  tree: () => [...orgUnitKeys.all, "tree"] as const,
  list: (params?: object) => [...orgUnitKeys.all, "list", params] as const,
  detail: (id: string) => [...orgUnitKeys.all, id] as const,
  members: (id: string) => [...orgUnitKeys.all, id, "members"] as const,
  memberAppRoles: (unitId: string, userId: string, clientId: string) =>
    [...orgUnitKeys.all, unitId, "members", userId, "app-roles", clientId] as const,
};

export function useOrgUnitTree() {
  return useQuery({
    queryKey: orgUnitKeys.tree(),
    queryFn: () => orgUnitsApi.tree(),
    refetchInterval: 30_000,
  });
}

export function useOrgUnits(params?: { type?: string; parentId?: string; search?: string }) {
  return useQuery({
    queryKey: orgUnitKeys.list(params),
    queryFn: () => orgUnitsApi.list(params),
    refetchInterval: 30_000,
  });
}

export function useOrgUnit(id: string) {
  return useQuery({
    queryKey: orgUnitKeys.detail(id),
    queryFn: () => orgUnitsApi.get(id),
    enabled: !!id,
  });
}

export function useOrgUnitMembers(id: string) {
  return useQuery({
    queryKey: orgUnitKeys.members(id),
    queryFn: () => orgUnitsApi.getMembers(id),
    enabled: !!id,
    refetchInterval: 30_000,
  });
}

export function useCreateOrgUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: orgUnitsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgUnitKeys.all });
    },
  });
}

export function useUpdateOrgUnit(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof orgUnitsApi.update>[1]) => orgUnitsApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgUnitKeys.all });
    },
  });
}

export function useDeleteOrgUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: orgUnitsApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgUnitKeys.all });
    },
  });
}

export function useAddOrgUnitMember(unitId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => orgUnitsApi.addMember(unitId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgUnitKeys.members(unitId) });
    },
  });
}

export function useRemoveOrgUnitMember(unitId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => orgUnitsApi.removeMember(unitId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgUnitKeys.members(unitId) });
    },
  });
}

export function useMemberAppRoles(unitId: string, userId: string, clientId: string) {
  return useQuery({
    queryKey: orgUnitKeys.memberAppRoles(unitId, userId, clientId),
    queryFn: () => orgUnitsApi.getMemberAppRoles(unitId, userId, clientId),
    enabled: !!unitId && !!userId && !!clientId,
  });
}

export function useBatchMemberAppRoles(unitId: string, clientIds: string[]) {
  return useQuery({
    queryKey: [...orgUnitKeys.all, unitId, "batch-app-roles", clientIds],
    queryFn: () => orgUnitsApi.batchMemberAppRoles(unitId, clientIds),
    enabled: !!unitId && clientIds.length > 0,
    refetchInterval: 30_000,
  });
}

export function useAssignMemberAppRoles(unitId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, clientId, roles }: { userId: string; clientId: string; roles: string[] }) =>
      orgUnitsApi.assignMemberAppRoles(unitId, userId, { clientId, roles }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgUnitKeys.all });
    },
  });
}

export function useRemoveMemberAppRoles(unitId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, clientId, roles }: { userId: string; clientId: string; roles: string[] }) =>
      orgUnitsApi.removeMemberAppRoles(unitId, userId, { clientId, roles }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgUnitKeys.all });
    },
  });
}

export function useSyncFromKeycloak() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => orgUnitsApi.syncFromKeycloak(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgUnitKeys.all });
    },
  });
}

export function useOrgTeams(keycloakOrgId: string | undefined) {
  return useQuery({
    queryKey: [...orgUnitKeys.all, "by-org", keycloakOrgId],
    queryFn: () => orgUnitsApi.listByOrg(keycloakOrgId!, "team"),
    enabled: !!keycloakOrgId,
    refetchInterval: 30_000,
  });
}

export function useOrgCompanyUnit(keycloakOrgId: string | undefined) {
  return useQuery({
    queryKey: [...orgUnitKeys.all, "by-org", keycloakOrgId, "company"],
    queryFn: () => orgUnitsApi.listByOrg(keycloakOrgId!, "company"),
    enabled: !!keycloakOrgId,
    select: (data) => data.items[0] ?? null,
  });
}

export function useEnsureCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { keycloakOrgId: string; name: string; slug: string }) =>
      orgUnitsApi.ensureCompany(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgUnitKeys.all });
    },
  });
}
